import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type { IEmployeeSuggestedSkill } from '@sharedInterfaces/IEmployee';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import getSuggestedSkills from '@src/APIs/graphQl/Employee/Suggestions/getSuggestedSkills';
import hideSuggestedSkill from '@src/APIs/graphQl/Employee/Suggestions/hideSuggestedSkill';
import changeMapSkill from '@src/APIs/graphQl/Skill/changeMapSkill';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import SuggestedSkillRateBar from '@src/App/Components/General/FormularElements/formsControls/inputs/SuggestedSkillRateBar/SuggestedSkillRateBar';
import { setEmployeeUnknownSkillCount } from '@store/reducer/employeeReducer';
import type { AppState } from '@store/store';
import { ExternalLink, EyeOff, Lightbulb, Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

/**
 * Renders a component for displaying suggested skills.
 *
 * @returns {JSX.Element}
 */
export function SuggestedSkillsPreview(): JSX.Element {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeDevelopment;
    const empSkills = useSelector((state: AppState) => state.employee.skills);
    const [hiddenSkills, setHiddenSkills] = useState<number[]>([]);

    const [suggestedSkills, setSuggestedSkills] = useState<IEmployeeSuggestedSkill[]>([]);
    const filterSuggestions = (suggestions: IEmployeeSuggestedSkill[]) =>
        suggestions.filter(
            (suggestion) => !empSkills.some((e) => e.id === suggestion.id) && !hiddenSkills.includes(suggestion.id),
        );
    const getAndUpdateSkills = () =>
        getSuggestedSkills((suggestedSkills) => {
            setSuggestedSkills(filterSuggestions(suggestedSkills));
        });
    useEffect(() => {
        getAndUpdateSkills();
    }, []);

    useEffect(() => {
        const filted = filterSuggestions(suggestedSkills);
        setSuggestedSkills(filted);
        if (filted.length <= 2) {
            getAndUpdateSkills();
        }
    }, [empSkills, empSkills.length, hiddenSkills, hiddenSkills.length]);

    if (suggestedSkills.length === 0) return <></>;

    return (
        <Paper
            elevation={0}
            sx={{
                p: { lg: 3, md: 2, xs: 2 },
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.02),
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Lightbulb size={20} color={theme.palette.primary.main} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {langStrings.suggestedSkills}
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    title={langStrings.showAllSuggestedSkills}
                    onClick={() => navigate(`/${ELinks.PROFILE_EDIT_SKILLS_SUGGESTIONS}`)}
                    sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                        },
                    }}
                >
                    <ExternalLink size={16} />
                </IconButton>
            </Box>

            {/* Skills Container */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'auto',
                    pb: 2,
                    mb: 1,
                    '&::-webkit-scrollbar': {
                        height: 8,
                    },
                    '&::-webkit-scrollbar-track': {
                        bgcolor: alpha(theme.palette.divider, 0.1),
                        borderRadius: 3,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        bgcolor: alpha(theme.palette.primary.main, 0.3),
                        borderRadius: 3,
                        '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.5),
                        },
                    },
                }}
            >
                {suggestedSkills.slice(0, 5).map((skill, index) => (
                    <SuggestedSkill
                        key={skill.id}
                        hiddenSkills={hiddenSkills}
                        setHiddenSkills={setHiddenSkills}
                        skill={skill}
                        compact={true}
                    />
                ))}
            </Box>
        </Paper>
    );
}
/**
 * Represents a suggested skill.
 *
 * @param skill - The suggested skill.
 * @returns The JSX element representing the suggested skill.
 */
function SuggestedSkill({
    setHiddenSkills,
    hiddenSkills,
    skill,
    compact = false,
}: {
    setHiddenSkills: React.Dispatch<React.SetStateAction<number[]>>;
    hiddenSkills: number[];
    skill: IEmployeeSuggestedSkill;
    compact?: boolean;
}) {
    const theme = useTheme();
    const showErrorDialog = useErrorDialog();
    const dispatch = useDispatch();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeDevelopment;
    const employee = useSelector((state: AppState) => state.employee);
    const offline = useSelector((state: AppState) => state.client.offline);
    const [saving, setSaving] = React.useState(false);

    if (!employee) return <></>;

    const countDown = () => {
        if (employee.unknownSkillCount > 0) dispatch(setEmployeeUnknownSkillCount(employee.unknownSkillCount - 1));
        setHiddenSkills([...hiddenSkills, skill.id]);
    };

    const handleAddSkill = (level: number) => {
        setSaving(true);
        changeMapSkill(skill.id, true, level)
            .then(() => {
                setSaving(false);
                countDown();
            })
            .catch((ex) => {
                showErrorDialog(ex, `${langStrings.errorMapSkill}: ${skill.title} - ${skill.id}`);
                setSaving(false);
            });
    };

    const handleHideSkill = () => {
        setSaving(true);
        hideSuggestedSkill(skill.id)
            .then(() => {
                setSaving(false);
                countDown();
            })
            .catch((ex) => {
                showErrorDialog(ex);
                setSaving(false);
            });
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { lg: 3, md: 2, xs: 2 },
                minWidth: compact ? 280 : 350,
                maxWidth: compact ? 320 : 400,
                flexShrink: 0,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                bgcolor: 'background.paper',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                },
            }}
        >
            {/* Left accent bar */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 4,
                    height: '100%',
                    bgcolor: theme.palette.primary.main,
                }}
            />

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Typography
                    variant={compact ? 'subtitle1' : 'h6'}
                    sx={{
                        fontWeight: 600,
                        flex: 1,
                        mr: 1,
                        lineHeight: 1.3,
                    }}
                >
                    {skill.title}
                </Typography>
                <IconButton
                    size="small"
                    disabled={offline}
                    title={langStrings.hideSkillSuggestion}
                    onClick={handleHideSkill}
                    sx={{
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                        '&:hover': {
                            bgcolor: alpha(theme.palette.error.main, 0.2),
                        },
                    }}
                >
                    <EyeOff size={16} />
                </IconButton>
            </Box>

            {/* Description */}
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                    mb: 3,
                    display: '-webkit-box',
                    WebkitLineClamp: compact ? 2 : 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.4,
                }}
            >
                {skill.description}
            </Typography>

            {/* Level Selection */}
            <Box>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                    {langStrings.skillFastSelect}
                </Typography>
                {saving ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                            {langStrings.saving}
                        </Typography>
                    </Box>
                ) : (
                    <SuggestedSkillRateBar onAdd={handleAddSkill} onHide={handleHideSkill} disabled={offline} />
                )}
            </Box>
        </Paper>
    );
}

export function SuggestedSkills(): JSX.Element {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeDevelopment;
    const empSkills = useSelector((state: AppState) => state.employee.skills);
    const [hiddenSkills, setHiddenSkills] = useState<number[]>([]);

    const [suggestedSkills, setSuggestedSkills] = useState<IEmployeeSuggestedSkill[] | null>(null);
    const filterSuggestions = (suggestions: IEmployeeSuggestedSkill[]) =>
        suggestions.filter(
            (suggestion) => !empSkills.some((e) => e.id === suggestion.id) && !hiddenSkills.includes(suggestion.id),
        );
    const getAndUpdateSkills = () =>
        getSuggestedSkills((suggestedSkills) => {
            setSuggestedSkills(filterSuggestions(suggestedSkills));
        }, true);
    useEffect(() => {
        getAndUpdateSkills();
    }, []);

    useEffect(() => {
        if (!suggestedSkills) return;
        const filted = filterSuggestions(suggestedSkills);
        setSuggestedSkills(filted);
    }, [empSkills, empSkills.length, hiddenSkills, hiddenSkills.length]);

    return (
        <Paper
            elevation={0}
            sx={{
                p: { lg: 3, md: 2, xs: 2 },
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Lightbulb size={24} color={theme.palette.primary.main} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {langStrings.suggestedSkills}
                </Typography>
            </Box>

            {/* Content */}
            {suggestedSkills === null ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {suggestedSkills.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Lightbulb size={48} color={theme.palette.text.disabled} style={{ marginBottom: 16 }} />
                            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                {langStrings.noSuggestedSkills}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Alle verfügbaren Skill-Vorschläge wurden bereits hinzugefügt oder ausgeblendet.
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'repeat(2, 1fr)',
                                    lg: 'repeat(3, 1fr)',
                                },
                                gap: 3,
                            }}
                        >
                            {suggestedSkills.map((skill) => (
                                <SuggestedSkill
                                    key={skill.id}
                                    hiddenSkills={hiddenSkills}
                                    setHiddenSkills={setHiddenSkills}
                                    skill={skill}
                                />
                            ))}
                        </Box>
                    )}
                </>
            )}
        </Paper>
    );
}
